import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export interface RadioCardPickerOption<T extends string> {
    value: T;
    label: string;
    description?: string;
    /** Greys out and locks this card alone; `disabled` on the picker locks every card. */
    disabled?: boolean;
}

export interface RadioCardPickerProps<T extends string> {
    /** The picked value. Anything that matches no option renders the group with nothing selected. */
    value: T;
    options: RadioCardPickerOption<T>[];
    onChange: (value: T) => void;
    ariaLabel: string;
    disabled?: boolean;
}

/**
 * The dashboard's radio cards: a row of equal columns, each one a label with a secondary line and
 * the radio on its right. Shown in the MUI components test page; used wherever a choice is small
 * enough to be laid out rather than hidden in a select.
 */
const RadioCardPicker = <T extends string, >({
    value,
    options,
    onChange,
    ariaLabel,
    disabled
}: RadioCardPickerProps<T>) => (
    <RadioGroup
        row
        value={value}
        onChange={event => onChange(event.target.value as T)}
        aria-label={ariaLabel}
        sx={{
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', sm: `repeat(${options.length}, minmax(0, 1fr))`},
            gap: 1,
            width: '100%'
        }}
    >
        {options.map(option => {
            const selected = option.value === value;
            const optionDisabled = disabled || option.disabled;

            return (
                <Box
                    key={option.value}
                    component={'label'}
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 2,
                        minHeight: 56,
                        px: 2,
                        py: 1.25,
                        border: '1px solid',
                        borderColor: selected ? 'text.primary' : 'divider',
                        borderRadius: 1,
                        boxShadow: selected ? 'inset 0 0 0 1px currentColor' : 'none',
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        cursor: optionDisabled ? 'default' : 'pointer',
                        opacity: optionDisabled ? 0.6 : 1,
                        transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
                        ...(!optionDisabled && {
                            '&:hover': {
                                bgcolor: 'action.hover',
                                borderColor: selected ? 'text.primary' : 'text.secondary'
                            }
                        })
                    }}
                >
                    <Stack minWidth={0} flex={1}>
                        <Typography variant={'body1'} sx={{fontWeight: 500}}>
                            {option.label}
                        </Typography>

                        {!!option.description && (
                            <Typography variant={'body2'} color={'text.secondary'}>
                                {option.description}
                            </Typography>
                        )}
                    </Stack>

                    <Radio
                        value={option.value}
                        checked={selected}
                        disabled={optionDisabled}
                        sx={{
                            p: 0,
                            alignSelf: 'flex-start',
                            color: 'divider',
                            '&.Mui-checked': {
                                color: 'text.primary'
                            }
                        }}
                    />
                </Box>
            );
        })}
    </RadioGroup>
);

export default RadioCardPicker;
